import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosGet,
  axiosPost,
  axiosPut,
  axiosDelete,
  axiosFormPost,
  reactQueryDefaultOptions,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

export const useQueryGetBrokerUsersList = () => {
  const getList = async () => {
    return await axiosGet(SERVICE_URLS.BROKER_USER_LIST);
  };
  return useQuery(
    [QUERY_KEYS.BROKER_USER_LIST],
    getList,
    reactQueryDefaultOptions,
  );
};

export const useQueryGetBrokerUsersPage = (page, size, sort) => {
  const getPage = async () => {
    return await axiosGet(SERVICE_URLS.BROKER_USER_PAGE(page, size, sort));
  };

  return useQuery([QUERY_KEYS.BROKER_USER_PAGE, page, size, sort], getPage, {
    ...reactQueryDefaultOptions,
    keepPreviousData: true,
  });
};

export const useQueryGetBrokerUserClients = (userId) => {
  const getList = async () => {
    return await axiosGet(SERVICE_URLS.BROKER_USER_CLIENTS(userId));
  };
  return useQuery(
    [QUERY_KEYS.BROKER_USER_CLIENTS, userId],
    getList,
    reactQueryDefaultOptions,
  );
};

export const useMutatePostBrokerUser = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.BROKER_USER_POST, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_USER_LIST]);
    },
  });
};

export const useMutateDeleteBrokerUser = () => {
  const deleteData = async (data) => {
    return await axiosDelete(SERVICE_URLS.BROKER_USER_DELETE, data);
  };

  var queryClient = useQueryClient();

  return useMutation(deleteData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_USER_LIST]);
    },
  });
};

export const useMutatePutBrokerUser = () => {
  const putData = async (data) => {
    return await axiosPut(SERVICE_URLS.BROKER_USER_PUT, data);
  };

  var queryClient = useQueryClient();

  return useMutation(putData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_USER_LIST]);
    },
  });
};

export const useMutateValidateBrokerImport = () => {
  const upload = async (file) => {
    const form = new FormData();
    form.append("data", file, file.name);
    return await axiosFormPost(SERVICE_URLS.BROKER_IMPORT_VALIDATE, form);
  };

  const queryClient = useQueryClient();

  return useMutation(upload, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_IMPORT_LIST]);
    },
  });
};

export const useQueryGetBrokerImportBatches = (take = 25) => {
  const getList = async () => {
    return await axiosGet(SERVICE_URLS.BROKER_IMPORT_LIST(take));
  };

  return useQuery(
    [QUERY_KEYS.BROKER_IMPORT_LIST, take],
    getList,
    reactQueryDefaultOptions,
  );
};

// Polls every 2s while the batch is Applying so the modal picks up
// per-row progress written by the queue-driven orchestration.
export const useQueryGetBrokerImportBatch = (id, { enabled = true } = {}) => {
  const getBatch = async () => {
    return await axiosGet(SERVICE_URLS.BROKER_IMPORT_GET(id));
  };

  return useQuery([QUERY_KEYS.BROKER_IMPORT_BATCH, id], getBatch, {
    ...reactQueryDefaultOptions,
    enabled: enabled && !!id,
    refetchInterval: (data) => {
      const status = data?.data?.status;
      return status === "Applying" ? 2000 : false;
    },
  });
};

export const useMutateApplyBrokerImport = () => {
  const apply = async (id) => {
    return await axiosPost(SERVICE_URLS.BROKER_IMPORT_APPLY(id), {});
  };

  const queryClient = useQueryClient();

  return useMutation(apply, {
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_IMPORT_BATCH, id]);
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_IMPORT_LIST]);
      queryClient.invalidateQueries([QUERY_KEYS.BROKER_USER_LIST]);
    },
  });
};
